import React, { useEffect } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

// Check environment variables directly as a fallback
const isDevelopment = process.env.NODE_ENV === 'development';
const skipAuthEnv = isDevelopment && process.env.REACT_APP_SKIP_AUTH === 'true';
const useEntraInDevEnv = isDevelopment && process.env.REACT_APP_USE_ENTRA_IN_DEV === 'true';

const ProtectedRoute = () => {
  const { isAuthenticated, loading, devMode, authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    console.log("ProtectedRoute state:", { 
      isAuthenticated, 
      loading, 
      devMode, 
      skipAuthEnv,
      useEntraInDevEnv,
      path: location.pathname 
    });
  }, [isAuthenticated, loading, devMode, location.pathname]);
  
  // Force navigation to dashboard if in dev mode with auth disabled
  useEffect(() => {
    // Check both the context value and direct env variables
    // Allow access if in dev mode with auth skipped but not if using Entra ID in dev 
    if ((devMode || skipAuthEnv) && !useEntraInDevEnv) {
      console.log("Development mode detected in ProtectedRoute, allowing access");
    }
  }, [devMode, navigate]);

  // Show loading indicator
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="textSecondary">
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // If there's an auth error, show it before redirecting
  // Skip this check in development mode with auth disabled (unless using Entra ID)
  if (authError && !isAuthenticated && ((!devMode && !skipAuthEnv) || useEntraInDevEnv)) {
    console.log("Authentication error detected, redirecting to login");
    // We can add a delay here if needed to show the error message
    return <Navigate to="/login" replace state={{ from: location, error: authError }} />;
  }

  // If user is not authenticated and not in dev mode (or if using Entra ID in dev), redirect to login
  if (!isAuthenticated && ((!devMode && !skipAuthEnv) || useEntraInDevEnv)) {
    console.log("Not authenticated, redirecting to login");
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // If authenticated or in dev mode, show the protected content
  console.log("Access granted to protected route:", location.pathname);
  return <Outlet />;
};

export default ProtectedRoute; 